/**
 * AttendanceConcernsWidget — tenant-wide (Admin/Owner). Reads
 * GET /v1/dashboard/attendance-concerns (new in this pass — the teacher
 * "My Day" widget already had a scoped source via /v1/dashboard/my-day, but
 * there was no tenant-wide equivalent for the admin/owner dashboard until
 * now). Also shows the staff attendance summary line for today via the
 * existing 11E.6 all-staff summary endpoint (month-granularity — no new
 * backend aggregation invented, see the widget's own note below).
 *
 * data-testid: candeza-dash-attendance-concerns
 */

import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Card, CardHeader, CardTitle, Badge } from '@/components/common';
import { useAttendanceConcerns } from '@/api/dashboard';
import { useStaffAttendanceSummary } from '@features/attendance/api/staffAttendance';

const ConcernsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const ConcernRow = styled(Link)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  text-decoration: none;
  color: inherit;
`;

const ConcernName = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const ConcernMeta = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const SummaryLine = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  padding-top: ${({ theme }) => theme.spacing.sm};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const EmptyNote = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: ${({ theme }) => theme.spacing.sm} 0;
  margin: 0;
`;

export const AttendanceConcernsWidget: React.FC = () => {
  const { data, isLoading } = useAttendanceConcerns();
  const now = new Date();
  const { data: staffSummary } = useStaffAttendanceSummary(now.getFullYear(), now.getMonth() + 1);

  const concerns = data?.concerns ?? [];
  const totalStaff = staffSummary?.rows.length ?? 0;
  const markedStaff = staffSummary?.rows.filter((r) => r.present_days > 0 || r.absent_days > 0).length ?? 0;

  return (
    <Card padding="lg" data-testid="candeza-dash-attendance-concerns">
      <CardHeader>
        <CardTitle>Attendance Concerns</CardTitle>
        {!isLoading && <Badge variant={concerns.length > 0 ? 'warning' : 'success'}>{concerns.length}</Badge>}
      </CardHeader>
      {isLoading && <EmptyNote>Loading…</EmptyNote>}
      {!isLoading && concerns.length === 0 && <EmptyNote>No attendance concerns right now.</EmptyNote>}
      {!isLoading && concerns.length > 0 && (
        <ConcernsList>
          {concerns.map((c) => (
            <ConcernRow key={c.student_id} to={`/students/${c.student_id}`}>
              <ConcernName>{c.student_name}</ConcernName>
              <ConcernMeta>
                {c.reason === 'consecutive' && `${c.consecutive_missed} missed in a row`}
                {c.reason === 'percentage' && `${c.pct_present ?? '—'}% present`}
                {c.reason === 'both' && `${c.consecutive_missed} missed · ${c.pct_present ?? '—'}%`}
              </ConcernMeta>
            </ConcernRow>
          ))}
        </ConcernsList>
      )}
      {staffSummary && totalStaff > 0 && (
        <SummaryLine data-testid="candeza-dash-staff-attendance-summary-line">
          Staff attendance this month: {markedStaff}/{totalStaff} with a marked day
        </SummaryLine>
      )}
    </Card>
  );
};
